import React, { useState } from "react";
import { FaUniversity } from "react-icons/fa";
import { Link } from "react-router-dom";
import { useAuth } from "../../contexts/AuthContext";
import { useToast } from "../../contexts/ToastContext";
import { useWishlist } from "../../hooks/useWishlist";
import { Course } from "../../types/course";

import type { University as UniversityType } from "../../types/university";

interface CourseCardProps {
  course: Course;
  university?: UniversityType;
}

const CourseCard: React.FC<CourseCardProps> = ({ course, university }) => {
  const { user, isAuthenticated } = useAuth();
  const { addToWishlist, removeFromWishlist, isInWishlist } = useWishlist();
  const { showSuccess, showError } = useToast();
  const [isProcessing, setIsProcessing] = useState(false);

  // Check if user is authenticated and not admin, and if course is in wishlist
  const showWishlistIcon = isAuthenticated && user && user.role !== "ADMIN";
  const courseInWishlist = showWishlistIcon ? isInWishlist(course.id) : false;

  const handleWishlistToggle = async (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();

    if (!showWishlistIcon) return;

    setIsProcessing(true);
    try {
      if (courseInWishlist) {
        const success = await removeFromWishlist(course.id);
        if (success) {
          showSuccess("Course removed from wishlist");
        }
      } else {
        const success = await addToWishlist(course.id);
        if (success) {
          showSuccess("Course added to wishlist");
        }
      }
    } catch (error) {
      showError("Failed to update wishlist");
    } finally {
      setIsProcessing(false);
    }
  };
  const getLevelBadgeColor = (level: string) => {
    switch (level) {
      case "BACHELOR":
        return "bg-cyan-100 text-cyan-800";
      case "MASTER":
        return "bg-orange-100 text-orange-800";
      case "KOREAN_LANGUAGE_PROGRAM":
        return "bg-purple-100 text-purple-800";
      default:
        return "bg-gray-100 text-gray-800";
    }
  };

  const formatLevel = (level: string) => {
    switch (level) {
      case "BACHELOR":
        return "Bachelor";
      case "MASTER":
        return "Master";
      case "KOREAN_LANGUAGE_PROGRAM":
        return "Language Program";
      default:
        return level;
    }
  };

  const UniversityIcon = FaUniversity as unknown as React.FC<{
    className?: string;
  }>;

  return (
    <div className="bg-white rounded-lg shadow-md hover:shadow-lg transition-shadow duration-300 overflow-hidden border border-gray-200">
      <div className="p-6">
        {/* Header */}
        <div className="flex justify-between items-start mb-3">
          <span
            className={`px-3 py-1 rounded-full text-sm font-medium ${getLevelBadgeColor(
              course.level
            )}`}
          >
            {formatLevel(course.level)}
          </span>
          <div className="flex items-center space-x-2">
            <span className="text-sm text-gray-500">
              {course.duration} years
            </span>
            {showWishlistIcon && (
              <button
                onClick={handleWishlistToggle}
                disabled={isProcessing}
                className={`p-1 rounded-full transition-colors duration-200 ${isProcessing
                  ? "opacity-50 cursor-not-allowed"
                  : "hover:bg-gray-100"
                  }`}
                title={
                  courseInWishlist ? "Remove from wishlist" : "Add to wishlist"
                }
              >
                {isProcessing ? (
                  <div className="w-5 h-5 border-2 border-gray-300 border-t-gray-600 rounded-full animate-spin"></div>
                ) : (
                  <svg
                    className={`w-5 h-5 transition-colors duration-200 ${courseInWishlist
                      ? "text-red-500 fill-current"
                      : "text-gray-400 hover:text-red-500"
                      }`}
                    fill={courseInWishlist ? "currentColor" : "none"}
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z"
                    />
                  </svg>
                )}
              </button>
            )}
          </div>
        </div>

        {/* Course Title */}
        <h3 className="text-xl font-bold text-gray-900 mb-2 line-clamp-2">
          {course.title}
        </h3>

        {/* University Info */}
        <div className="flex items-center mb-4">
          <div className="w-10 h-10 bg-gradient-to-r from-cyan-500 to-blue-500 rounded-full flex items-center justify-center mr-3 flex-shrink-0">
            {course.university?.logoUrl || university?.logoUrl ? (
              <img
                src={course.university?.logoUrl || university?.logoUrl || ""}
                alt={course.university?.name || university?.name || "University logo"}
                className="w-10 h-10 rounded-full object-cover"
              />
            ) : (
              <UniversityIcon className="text-white text-lg" />
            )}
          </div>
          <div>
            <p className="font-medium text-gray-900">
              {course.university?.name || university?.name || "University"}
            </p>
            <p className="text-sm text-gray-600">
              {course.university?.city || university?.city || ""}
              {course.university?.country || university?.country
                ? `, ${course.university?.country || university?.country}`
                : ""}
            </p>
          </div>
        </div>

        {/* Course Details */}
        <div className="space-y-2 mb-4">
          <div className="flex justify-between text-sm">
            <span className="text-gray-600">Language:</span>
            <span className="font-medium text-gray-900">{course.language}</span>
          </div>
          <div className="flex justify-between text-sm">
            <span className="text-gray-600">Study Mode:</span>
            <span className="font-medium text-gray-900">
              {course.studyMode.replace("_", " ")}
            </span>
          </div>
        </div>

        {/* Scholarship Info */}
        {course.firstSemesterScholarship &&
          course.firstSemesterScholarship !== "-" && (
            <div className="mb-4">
              <div className="flex items-center mb-2">
                <div className="w-2 h-2 bg-orange-500 rounded-full mr-2"></div>
                <span className="text-sm font-medium text-gray-700">
                  Scholarship Available
                </span>
              </div>
              <p className="text-xs text-gray-600 line-clamp-2">
                {course.firstSemesterScholarship.split("\n")[0]}
              </p>
            </div>
          )}

        {/* Action Button */}
        <Link
          to={`/courses/${course.id}`}
          className="block w-full bg-gradient-to-r from-cyan-500 to-blue-500 text-white py-2 px-4 rounded-lg font-medium hover:from-cyan-600 hover:to-blue-600 transition-colors duration-200 text-center"
        >
          View Details
        </Link>
      </div>
    </div>
  );
};

export default CourseCard;
